import {isUndefined} from 'lodash';

import {RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownResults, DropdownSource, RowHeightPredicate, RowRendererOf} from './dropdownSource';

export type DropdownSourceFilter<T> = (items: ReadonlyArray<T>, query: string) => ReadonlyArray<T>;

export interface SyncDropdownSourceOptions<T> {
  items: ReadonlyArray<T>;
  rowFilter: DropdownSourceFilter<T>;
  renderRow: RowRendererOf<T>;
  renderEmpty?: RendererOf<string>;
  selectableRowCount?: number;
  rowHeightPredicate?: RowHeightPredicate<T>;

  /* Optional key to indicate results may have semantically changed outside of search query changes. */
  readonly refreshKey?: string;
}

export class SyncDropdownSource<T> implements DropdownSource {
  constructor(options: SyncDropdownSourceOptions<T>) {
    this.items = options.items;
    this.rowFilter = options.rowFilter;
    this.rowRenderer = options.renderRow;
    this.emptyRenderer = options.renderEmpty;
    this.selectableRowCount = options.selectableRowCount;
    this.rowHeightPredicate = options.rowHeightPredicate;
    this.refreshKey = options.refreshKey;
  }

  private readonly items: ReadonlyArray<T>;
  private readonly rowFilter: DropdownSourceFilter<T>;
  private readonly rowRenderer: RowRendererOf<T>;
  private readonly emptyRenderer?: RendererOf<string>;
  private readonly selectableRowCount?: number;
  private readonly rowHeightPredicate?: RowHeightPredicate<T>;
  private readonly refreshKey?: string;

  async searchAsync(query: string) {
    const filteredItems = this.rowFilter(this.items, query);
    return new SyncDropdownResults<T>(
      this.rowRenderer,
      this.emptyRenderer,
      query,
      filteredItems,
      this.selectableRowCount,
      this.rowHeightPredicate,
      this.refreshKey,
    );
  }
}

class SyncDropdownResults<T> implements DropdownResults {
  constructor(
    rowRenderer: RowRendererOf<T>,
    emptyRenderer: RendererOf<string> | undefined,
    query: string,
    filteredItems: ReadonlyArray<T>,
    selectableRowCount?: number,
    rowHeightPredicate?: RowHeightPredicate<T>,
    refreshKey?: string,
  ) {
    this.rowRenderer = rowRenderer;
    this.emptyRenderer = emptyRenderer;
    this.query = query;
    this.filteredItems = filteredItems;
    this.selectableRowCount = selectableRowCount;
    this.rowHeightPredicate = rowHeightPredicate;
    this.canLoadMore = false;
    this.refreshKey = refreshKey;
  }

  private readonly rowRenderer: RowRendererOf<T>;
  private readonly emptyRenderer?: RendererOf<string>;
  private readonly rowHeightPredicate?: RowHeightPredicate<T>;
  private readonly filteredItems: ReadonlyArray<T>;

  get rowCount() {
    return this.filteredItems.length;
  }

  readonly query: string;
  readonly selectableRowCount?: number;
  readonly canLoadMore: boolean;
  readonly refreshKey?: string;

  renderRow(index: number) {
    const item = this.filteredItems[index];
    if (isUndefined(item)) {
      throw new Error('An item at the specified index could not be found.');
    }

    return this.rowRenderer(item, index, this.filteredItems, this.query.length > 0);
  }

  getRowHeight(index: number) {
    if (!this.rowHeightPredicate) {
      return undefined;
    }

    const item = this.filteredItems[index];
    if (isUndefined(item)) {
      throw new Error('An item at the specified index could not be found.');
    }
    return this.rowHeightPredicate(item, index, this.filteredItems, this.query.length > 0);
  }

  renderEmpty() {
    return this.emptyRenderer && this.emptyRenderer(this.query);
  }
}
